import * as _ from 'underscore';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { Module } from '../Module';
import { CreateLocalServerUserRequest } from '../ServerApi/requests/CreateLocalServerUserRequest';
import {
  CreateLocalServerUserWithoutPasswordRequest,
} from '../ServerApi/requests/CreateLocalServerUserWithoutPasswordRequest';
import { DeleteServerUsersRequest } from '../ServerApi/requests/DeleteServerUsersRequest';
import {
  DeleteUserConnectedDevicesRequest,
  IDeleteUserConnectedDevicesParams,
} from '../ServerApi/requests/DeleteUserConnectedDevicesRequest';
import {
  DeleteUserRemoteAgentsRequest,
  IDeleteUserRemoteAgentsParams,
} from '../ServerApi/requests/DeleteUserRemoteAgentsRequest';
import { ForcePasswordUpdateOnSiteUsersRequest } from '../ServerApi/requests/ForcePasswordUpdateOnSiteUsers';
import { ForcePasswordUpdateRequest } from '../ServerApi/requests/ForcePasswordUpdateRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../ServerApi/requests/GeneratePublicKeyRequest';
import { GetPersonalAccessTokenClientIdsParams, GetPersonalAccessTokenClientIdsRequest, GetPersonalAccessTokenClientIdsResult } from '../ServerApi/requests/GetPersonalAccessTokenClientIdsRequest';
import { GetServerUsersRequest } from '../ServerApi/requests/GetServerUsersRequest';
import {
  GetUserConnectedDevicesRequest,
  IGetUserConnectedDevicesParams,
  IGetUserConnectedDevicesResult,
} from '../ServerApi/requests/GetUserConnectedDevicesRequest';
import {
  GetUserRefreshTokenCountRequest,
  IGetUserRefreshTokenCountParams,
  IGetUserRefreshTokenCountResult,
} from '../ServerApi/requests/GetUserRefreshTokenCountRequest';
import {
  GetUserSettingsRequest,
  IGetUserSettingsParams,
  IGetUserSettingsResult,
} from '../ServerApi/requests/GetUserSettingsRequest';
import { GetUsersSiteRolesRequest, IGetUsersSiteRolesResult } from '../ServerApi/requests/GetUsersSiteRolesRequest';
import { ImportServerUsersFromActiveDirectoryRequest } from '../ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { ResetLoginRateLimitingOnSiteUsersRequest } from '../ServerApi/requests/ResetLoginRateLimitingOnSiteUsersRequest';
import { ResetLoginRateLimitingRequest } from '../ServerApi/requests/ResetLoginRateLimitingRequest';
import { RevokePersonalAccessTokenByClientIdParams, RevokePersonalAccessTokenByClientIdRequest } from '../ServerApi/requests/RevokePersonalAccessTokenByClientIdRequest';
import { UpdateServerUsersAdminStatusRequest } from '../ServerApi/requests/UpdateServerUsersAdminStatusRequest';
import {
  IUpdateServerUsersSiteMembershipResult,
  UpdateServerUsersSiteMembershipRequest,
} from '../ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IConnectedDevice } from '../ServerApi/types/IConnectedDevice';
import { FilterFieldNames, IFieldFilterClause } from '../ServerApi/types/IFilterClause';
import { IPersonalAccessToken } from '../ServerApi/types/IPersonalAccessToken';
import { IResult } from '../ServerApi/types/IResponse';
import { ISiteRole } from '../ServerApi/types/ISiteRole';
import { IUserActionResult } from '../ServerApi/types/IUserActionResult';
import { NamedItemKeys } from '../ServerApi/types/resourceParts';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { Resource } from './Resource';
import { IResourceQuery } from './ResourceQuery';
import { SiteRolesResource } from './SiteRoles';

export class ServerUsers extends Resource<IServerUserInfo> {

  public static $inject = [
    'ErrorNotifyingServer',
    'FetcherFactory',
    'SiteRoles',
    '$q'
  ];

  constructor(private ErrorNotifyingServer: ErrorNotifyingServer,
              FetcherFactory: FetcherFactory,
              private siteRoles: SiteRolesResource,
              private $q: ng.IQService) {
    super(FetcherFactory.getServerResourceFetcher(GetServerUsersRequest, ServerApiMapper.serverUsers));
  }

  // returns the server user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): ng.IPromise<IServerUserInfo> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  // returns the server users with the specified IDs
  public getByUserIds(userIds: string[]): ng.IPromise<ListResult<IServerUserInfo>> {
    let query = this.all();
    const usersFilter = Filter.stringFieldInArray(NamedItemKeys.id, userIds);
    query = query.addFilter(usersFilter);

    return query.getCount().then((totalCount: number) => {
      return query.getSlice(0, totalCount);
    });
  }

  public getUserCountsByMaxSiteRole(baseQuery: IResourceQuery<IServerUserInfo>)
      : ng.IPromise<_.Dictionary<number>> {

    // Remove any existing max site role filter
    const filtersWithoutMaxSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.maxSiteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutMaxSiteRole);

    const counts: _.Dictionary<number> = {};

    const promises = _.map(this.siteRoles.optionsForCurrentUser(), (maxSiteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.maxSiteRole, maxSiteRole))
        .getCount()
        .then((count:number) => {
          counts[maxSiteRole] = count;
        });
    });

    // Any site role
    promises.push(modifiedQuery.getCount().then((count: number) => { counts['Any'] = count; }));

    return this.$q.all(promises).then(() => counts);
  }

  public getUserSettings(username: string, domain: string): ng.IPromise<IGetUserSettingsResult> {
    const params: IGetUserSettingsParams = {
      username: username,
      domainName: domain
    };
    return this.ErrorNotifyingServer.sendRequest(new GetUserSettingsRequest(params));
  }

  public getUserRefreshTokenCount(username: string, domain: string): ng.IPromise<number> {
    const params: IGetUserRefreshTokenCountParams = {
      username: username,
      domainName: domain
    };
    return this.ErrorNotifyingServer.sendRequest(new GetUserRefreshTokenCountRequest(params))
      .then((result: IGetUserRefreshTokenCountResult) => result.refreshTokenCount);
  }

  public getUserConnectedDevices(userId: string): ng.IPromise<IConnectedDevice[]> {
    const params: IGetUserConnectedDevicesParams = {
      userId: userId
    };
    return this.ErrorNotifyingServer.sendRequest(new GetUserConnectedDevicesRequest(params))
      .then((result: IGetUserConnectedDevicesResult) => result.devices);
  }

  public deleteUserConnectedDevices(userId: string, deviceIds: string[]): ng.IPromise<IResult> {
    const params: IDeleteUserConnectedDevicesParams = {
      userId: userId,
      deviceIds: deviceIds
    };
    return this.ErrorNotifyingServer.sendRequest(new DeleteUserConnectedDevicesRequest(params));
  }

  public getPersonalAccessTokens(userId: string): ng.IPromise<IPersonalAccessToken[]> {
    const params: GetPersonalAccessTokenClientIdsParams = {
      userId: userId
    };
    return this.ErrorNotifyingServer.sendRequest(new GetPersonalAccessTokenClientIdsRequest(params))
      .then((result: GetPersonalAccessTokenClientIdsResult) => result.tokenInfos);
  }

  public deletePersonalAccessToken(userId: string, tokenName: string): ng.IPromise<IResult> {
    const params: RevokePersonalAccessTokenByClientIdParams = {
      userId: userId,
      tokenName: tokenName
    };
    return this.ErrorNotifyingServer.sendRequest(new RevokePersonalAccessTokenByClientIdRequest(params));
  }

  public deleteUserRemoteAgents(ownerId: string, deviceIds: string[]): ng.IPromise<IResult> {
    const params: IDeleteUserRemoteAgentsParams = {
      ownerId: ownerId,
      deviceIds: deviceIds
    };
    return this.ErrorNotifyingServer.sendRequest(new DeleteUserRemoteAgentsRequest(params));
  }

  // returns the server users' roles across all sites
  public getSiteRoles(userIds: string[]): ng.IPromise<IGetUsersSiteRolesResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new GetUsersSiteRolesRequest({ ids: userIdsBlock })),
      (results: IGetUsersSiteRolesResult[]) => {
        return {
          errors: ResultHelper.combineErrors(results),
          users: ResultHelper.combine(results, (result) => result.users)
        };
      });
  }

  // site membership - add/remove server users from sites
  public updateServerUsersSiteMembership(userIds: string[], addToSiteRoles: ISiteRole[], removeFromSiteIds: string[])
      : ng.IPromise<IUpdateServerUsersSiteMembershipResult>
  {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToSiteRoles, removeFromSiteIds,
        (userIdsBlock, addToSiteRolesBlock, removeFromSiteIdsBlock) => new UpdateServerUsersSiteMembershipRequest({
          userIds: userIdsBlock,
          addToSiteRoles: addToSiteRolesBlock,
          removeFromSiteIds: removeFromSiteIdsBlock
        })),
        (results: IUpdateServerUsersSiteMembershipResult[]) => {
          return {
            results: ResultHelper.combine(results, (result) => result.results)
          };
      });
  }

  // site membership - change these users' server admin status
  public updateServerAdminStatus(userIds: string[], admin: boolean)
      : ng.IPromise<IResult>
  {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateServerUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // force a password update on the users provided
  public forcePasswordUpdate(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public forcePasswordUpdateOnSiteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the users provided
  public resetLoginRateLimiting(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public resetLoginRateLimitingOnSiteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // delete users from server
  public deleteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteServerUsersRequest({ ids: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // import users from active directory to server
  public importUsersFromActiveDirectory(usernames: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames,
        (usernamesBlock) => new ImportServerUsersFromActiveDirectoryRequest({usernames: usernamesBlock})),
      ResultHelper.combineImportUserResults);
  }

  // create a new local server user
  public createLocalServerUser(
    username: string,
    displayName: string,
    email: string,
    password: string): ng.IPromise<IResult> {

    return this.ErrorNotifyingServer.sendRequestAndDelay(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalServerUserRequest = new CreateLocalServerUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId
        });
        return this.ErrorNotifyingServer.sendRequestAndDelay(createLocalServerUserRequest);
      });
  }

    // create a new local server user when SAML or SiteSAML is enabled
  public createLocalServerUserWithoutPassword(
    username: string,
    displayName: string,
    email: string): ng.IPromise<IResult> {
        const createLocalServerRequest = new CreateLocalServerUserWithoutPasswordRequest({
          username: username,
          displayName: displayName,
          email: email
        });
        return this.ErrorNotifyingServer.sendRequestAndDelay(createLocalServerRequest);
  }
}

Module.instance().service('ServerUsers', ServerUsers);
